import { useRef, useState } from 'react';
import type { Feed, FeedEntry, UIComponent } from '~/services/layout/index.ts';
import { useNextFeedPagination } from '~/hooks/use-next-feed.ts';
import { useFeedEntries } from '~/hooks/use-feed-entries.ts';
import { PowerCell } from '../../power-cell/components';
import manifest from '../config/manifest';
import { getPluginConfig } from '~/utils/get-plugin-configuration';
import { useFeedEntriesOnSearchQueryChange } from '~/hooks/use-feed-entries-on-search';
import { useLastComponentInScreenPaginationCondition } from '~/hooks/use-pagination-condition';
import logger from '~/services/logger';
import { getUniqueKeyByEntry } from '~/services/layout/layout';
import { getComponentMarginsOnMaxWidth } from '~/utils/styles';
import { usePreferenceEditorPayload } from '~/hooks/preference-selector/use-preference-editor';
import type { StorageFeedsData } from '~/components/ui-component';
import type { UseIsLoggedInReturnType } from '~/hooks/use-is-logged-in';

export default function ListV2({
  uiComponent,
  feed,
  isLoading,
  isClient,
  isClientStorage,
  storageFeedsData,
  isLoggedInData,
}: {
  uiComponent: UIComponent;
  feed?: Feed;
  isLoading?: boolean;
  isClient?: boolean;
  isClientStorage?: boolean;
  storageFeedsData: StorageFeedsData;
  isLoggedInData: UseIsLoggedInReturnType;
}): JSX.Element {
  const { styles, rules } = getPluginConfig<typeof manifest>(
    manifest,
    uiComponent
  );

  const selectable: boolean = !!rules.component_cells_selectable;

  const cellContainerRefs = useRef<any>([]);

  const [feedEntries, setFeedEntries] = useState<FeedEntry[]>([]);

  const [nextFeedUrl, setNextFeedUrl] = useState<string>(feed?.next || '');

  const [isNextFeedLoading, setIsNextFeedLoading] = useState<boolean>(false);

  const itemLimit: number | undefined = rules?.item_limit;

  usePreferenceEditorPayload({
    feed,
    uiComponent,
    setFeedEntries,
  });

  const entries = useFeedEntries({
    feedEntries,
    setFeedEntries,
    isLoading,
    isClient,
    itemLimit,
    feed,
    uiComponent,
    isClientStorage,
  });

  useFeedEntriesOnSearchQueryChange({
    setFeedEntries,
    entries,
  });

  const paginationCondition = useLastComponentInScreenPaginationCondition({
    refs: cellContainerRefs,
    isLoading,
    uiComponentId: uiComponent?.id,
  });

  useNextFeedPagination({
    feed,
    nextFeedUrl,
    setNextFeedUrl,
    setIsNextFeedLoading,
    isNextFeedLoading,
    isLoading,
    setFeedEntries,
    feedEntries,
    itemLimit,
    condition: paginationCondition,
  });

  try {
    return (
      <div className="flex justify-evenly">
        <section
          className={`
          component-${
            uiComponent.id
          } component-classes list-v2-vertical-gutter flex h-fit w-full shrink-0 flex-col overflow-x-auto overflow-y-hidden border-none scrollbar-hide ${getComponentMarginsOnMaxWidth(
            { styles }
          )} component-maximum-width`}
        >
          {entries?.map((entry, index) => {
            const key: string = getUniqueKeyByEntry('list-v2', entry, index);

            return (
              <div
                ref={(el) => cellContainerRefs.current.push(el)}
                key={key}
                id={key}
              >
                <PowerCell
                  uiComponent={uiComponent}
                  entry={entry}
                  selectable={selectable}
                  isLoading={isLoading}
                  storageFeedsData={storageFeedsData}
                  isLoggedInData={isLoggedInData}
                />
              </div>
            );
          })}
        </section>
      </div>
    );
  } catch (error: any) {
    logger.info(`List V2: ${error.message}`);
    return <></>;
  }
}
